import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';

export default function useVehicleApplication(
  vehicleId: string,
  vehicleApplicationId: string,
) {
  const queryClient = useQueryClient();
  const queryKey = getQueryKey(
    api.onboarding.investorDetails,
    { vehicleId, vehicleApplicationId },
    'query',
  );
  const investorClassificationResultQueryKey = getQueryKey(
    api.investorClassification.getClassificationResult,
    { vehicleId, vehicleApplicationId },
    'query',
  );

  const [{ investor, vehicleApplication, bankDetails, taxDetails, users }] =
    api.onboarding.investorDetails.useSuspenseQuery(
      {
        vehicleId,
        vehicleApplicationId,
      },
      {
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    );

  const disableManualCommitmentFlow =
    api.vehicleApplication.disableManualCommitmentFlow.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Commitment deleted successfully.',
        invalidations: [queryKey],
      }),
    );

  const saveApplicationDetails =
    api.vehicleApplication.saveApplicationDetails.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Investor details saved successfully.',
        invalidations: [queryKey],
      }),
    );

  const saveBankAccountDetails =
    api.vehicleApplication.saveBankAccountDetails.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Bank information saved successfully.',
        invalidations: [queryKey],
      }),
    );

  const saveTaxDetails = api.vehicleApplication.saveTaxDetails.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Tax information saved successfully.',
      invalidations: [queryKey],
    }),
  );

  const saveIndividualInvestorDetails =
    api.vehicleApplication.saveIndividualInvestorDetails.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Investor information saved successfully.',
        invalidations: [queryKey],
      }),
    );

  const saveLegalEntityDetails =
    api.vehicleApplication.saveLegalEntityDetails.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Investor information saved successfully.',
        invalidations: [queryKey],
      }),
    );

  const updateInvestorJourney =
    api.vehicleApplication.updateInvestorJourney.useMutation({
      ...useHandleTrpcMutation()({
        successMessage: 'Investor journey updated successfully.',
        invalidations: [queryKey],
      }),
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey });
      },
    });

  const saveCommitmentSettings =
    api.vehicleApplication.saveCommitmentSettings.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Commitment details saved successfully.',
        invalidations: [queryKey],
      }),
    );

  const approveStep = api.vehicleApplication.approveStep.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Step approved successfully.',
      invalidations: [
        queryKey,
        investorClassificationResultQueryKey,
        getQueryKey(api.lpKyc.getInvestorKyc, {
          vehicleId,
          investorId: investor.id,
        }),
      ],
    }),
  );

  const unapproveStep = api.vehicleApplication.unapproveStep.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Step unapproved successfully.',
      invalidations: [
        queryKey,
        investorClassificationResultQueryKey,
        getQueryKey(api.lpKyc.getInvestorKyc, {
          vehicleId,
          investorId: investor.id,
        }),
      ],
    }),
  );

  const requestMoreInformation =
    api.vehicleApplication.requestMoreInformation.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Additional information requested successfully.',
        invalidations: [queryKey],
      }),
    );

  const isPending =
    disableManualCommitmentFlow.isPending ||
    saveApplicationDetails.isPending ||
    saveBankAccountDetails.isPending ||
    saveTaxDetails.isPending ||
    saveIndividualInvestorDetails.isPending ||
    saveLegalEntityDetails.isPending ||
    updateInvestorJourney.isPending ||
    saveCommitmentSettings.isPending ||
    approveStep.isPending ||
    unapproveStep.isPending ||
    requestMoreInformation.isPending;

  return {
    queryKey,
    //Data
    investor,
    vehicleApplication,
    bankDetails,
    taxDetails,
    users,

    //Actions
    disableManualCommitmentFlow: disableManualCommitmentFlow.mutate,
    saveApplicationDetails: saveApplicationDetails.mutate,
    saveBankAccountDetails: saveBankAccountDetails.mutate,
    saveTaxDetails: saveTaxDetails.mutate,
    saveIndividualInvestorDetails: saveIndividualInvestorDetails.mutate,
    saveLegalEntityDetails: saveLegalEntityDetails.mutate,
    updateInvestorJourney: updateInvestorJourney.mutate,
    saveCommitmentSettings: saveCommitmentSettings.mutate,
    approveStep: approveStep.mutate,
    unapproveStep: unapproveStep.mutate,
    requestMoreInformation: requestMoreInformation.mutate,
    //State
    isPending,
  };
}
